// src/data/directoryService.js
// Data service for directory_listings, directory_suggestions, and locality_admins.
// Follows the same supabaseFetch patterns as dataService.js.

import { supabaseFetch } from '../lib/supabase';
import { baseParishId } from '../utils/scopeResolution';
import { getPublicProfiles } from './profileService';

/**
 * Fetch the authority-category sort ranks for a country, from directory_authority_categories
 * (Scope A of the Official Contacts merged-ordering brief). Returns a plain {category: rank}
 * map so callers never need to know the table shape. A category absent from the map is
 * unranked — sortOfficialContactsByAuthority() puts it in the alphabetical tail, not first or
 * last by accident.
 * @param {string} countryCode
 * @returns {Promise<Object<string, number>>}
 */
export async function getAuthorityCategoryRanks(countryCode) {
  try {
    const { data, error } = await supabaseFetch.query('directory_authority_categories', {
      select: 'category,rank',
      eq: { country_code: countryCode }
    });
    if (error || !data) return {};
    return data.reduce((acc, r) => ({ ...acc, [r.category]: r.rank }), {});
  } catch (err) {
    console.error('getAuthorityCategoryRanks exception:', err);
    return {};
  }
}

// Ranked categories first (ascending rank), then everything else alphabetically by name — the
// Official Contacts merged-ordering ruling. A category with no entry in rankMap (e.g. 'official',
// 'health_centre') falls to the alphabetical tail; a ranked category with nothing occupying it
// (e.g. 'regional' before any distrito/regional contact is loaded) simply has no row to place —
// no placeholder, no gap. No category string is hardcoded here: rankMap is the only source of
// what counts as an authority. Alphabetical fallback matches the platform's existing sort idiom
// (dataService.js's searchCommunities group sort). Inside the same rank, and in the tail, nearer
// comes first and national last before the name decides (Roadmap #87, R2; compareByDistance).
export function sortOfficialContactsByAuthority(rows, rankMap) {
  return [...rows].sort((a, b) => {
    const rankA = rankMap[a.category];
    const rankB = rankMap[b.category];
    if (rankA != null && rankB != null && rankA !== rankB) return rankA - rankB;
    if (rankA != null && rankB == null) return -1;
    if (rankB != null && rankA == null) return 1;
    return compareByDistance(a, b) || (a.name || '').localeCompare(b.name || '');
  });
}

// R2's "local first": the area's own listings (distance 0), then wider ones nearest first, then
// national ones (distance NULL) last — the JS twin of the SQL's "distance NULLS LAST". Takes RAW
// rows (is_national / distance, as the RPC returns them), not useDirectoryListings' mapped
// isNational: directory rows carry both, contacts and providers carry the raw fields only before
// mapping, so sort before mapping. A null distance must never compare as 0, or a national listing
// would sort beside the area's own.
export function compareByDistance(a, b) {
  const da = a.is_national || a.distance == null ? Infinity : a.distance;
  const db = b.is_national || b.distance == null ? Infinity : b.distance;
  if (da === db) return 0;
  return da < db ? -1 : 1;
}

/**
 * Directory listings for a locality (Roadmap #87, R1–R2): in all three sections, the active
 * listings on the area, on every area above it, and the national listings for its country —
 * one RPC, get_directory_listings, ordered by section, category, distance (national last), name.
 * Each row carries area_name, distance (0 = own) and is_national, for the row's area label.
 * Official Contacts are then merged into one authority-ordered list
 * (sortOfficialContactsByAuthority). That needs the base area's country_code, which the RPC does
 * not return — so the light admin_boundaries read stays; removing it silently loses the ranking.
 *
 * @param {number[]} ownBoundaryIds - the locality's own scope ([boundaryId])
 * @param {number} baseBoundaryId - the base area, whose country_code selects the rank vocabulary
 * @returns {Promise<{officialContacts: Array, utilities: Array, directory: Array}>}
 */
export async function getDirectoryListingsWithAncestors(ownBoundaryIds, baseBoundaryId) {
  const empty = { officialContacts: [], utilities: [], directory: [] };
  try {
    const [{ data, error }, base] = await Promise.all([
      supabaseFetch.rpc('get_directory_listings', { p_boundary_ids: ownBoundaryIds }),
      baseBoundaryId
        ? supabaseFetch.query('admin_boundaries', { select: 'country_code', eq: { id: baseBoundaryId }, single: true })
        : Promise.resolve({ data: null })
    ]);
    if (error) {
      console.error('getDirectoryListingsWithAncestors error:', error);
      return empty;
    }
    const rows = data || [];
    const countryCode = base?.data?.country_code || null;
    const rankMap = countryCode ? await getAuthorityCategoryRanks(countryCode) : {};
    return {
      officialContacts: sortOfficialContactsByAuthority(rows.filter(r => r.section === 'official_contact'), rankMap),
      utilities: rows.filter(r => r.section === 'utility'),
      directory: rows.filter(r => r.section === 'directory')
    };
  } catch (err) {
    console.error('getDirectoryListingsWithAncestors exception:', err);
    return empty;
  }
}

/**
 * Pending directory suggestions the caller may decide (Roadmap #87, R6): the review queue and its
 * badge, from one source. get_reviewable_directory_suggestions applies the same rule as approve
 * and reject (can_decide_directory_suggestion): an admin; for a correction, a steward covering
 * the listing's own area; for a new listing, a steward covering the suggestion's area.
 * @returns {Promise<Array<{id, boundary_id, area_name, listing_id, listing_boundary_id,
 *   listing_is_national, suggested_by, payload, note, created_at}>>}
 */
export async function getReviewableSuggestions() {
  try {
    const { data, error } = await supabaseFetch.rpc('get_reviewable_directory_suggestions');
    if (error) {
      console.error('getReviewableSuggestions error:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('getReviewableSuggestions exception:', err);
    return [];
  }
}

/**
 * Submit a directory suggestion (new listing or correction).
 * Uses the submit_directory_suggestion RPC.
 */
export async function submitSuggestion({ boundaryId, listingId, payload, note }) {
  try {
    const { data, error } = await supabaseFetch.rpc('submit_directory_suggestion', {
      p_boundary_id: boundaryId,
      p_listing_id: listingId || null,
      p_payload: payload,
      p_note: note || null
    });
    if (error) {
      console.error('submitSuggestion error:', error);
      return { success: false, error };
    }
    return { success: true, id: data };
  } catch (err) {
    console.error('submitSuggestion exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Submit a directory suggestion from the PUBLIC (no-account) /suggest page.
 * Uses the anon-granted submit_directory_suggestion_public RPC — suggested_by
 * stays NULL server-side. Steward review is the firebreak (nothing goes live
 * until approved). Only creates NEW listings (no correction / listingId path).
 */
export async function submitPublicSuggestion({ boundaryId, payload, note }) {
  try {
    const { data, error } = await supabaseFetch.rpc('submit_directory_suggestion_public', {
      p_boundary_id: boundaryId,
      p_payload: payload,
      p_note: note || null
    });
    if (error) {
      console.error('submitPublicSuggestion error:', error);
      return { success: false, error };
    }
    return { success: true, id: data };
  } catch (err) {
    console.error('submitPublicSuggestion exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Approve a directory suggestion (admin action).
 */
export async function approveSuggestion(suggestionId) {
  try {
    const { data, error } = await supabaseFetch.rpc('approve_directory_suggestion', {
      p_suggestion_id: suggestionId
    });
    if (error) {
      console.error('approveSuggestion error:', error);
      return { success: false, error };
    }
    return { success: true, listingId: data };
  } catch (err) {
    console.error('approveSuggestion exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Reject a directory suggestion (admin action).
 */
export async function rejectSuggestion(suggestionId) {
  try {
    const { error } = await supabaseFetch.rpc('reject_directory_suggestion', {
      p_suggestion_id: suggestionId
    });
    if (error) {
      console.error('rejectSuggestion error:', error);
      return { success: false, error };
    }
    return { success: true };
  } catch (err) {
    console.error('rejectSuggestion exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Fetch all locality admins (superadmin view).
 * @returns {Promise<Array>}
 */
export async function getAllLocalityAdmins() {
  try {
    const { data, error } = await supabaseFetch.query('locality_admins', {
      select: 'id,user_id,boundary_id,granted_by,granted_at,is_active,role',
      eq: { is_active: true },
      order: 'granted_at.desc'
    });
    if (error) {
      console.error('getAllLocalityAdmins error:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('getAllLocalityAdmins exception:', err);
    return [];
  }
}

/**
 * Grant locality admin status via RPC.
 */
export async function grantLocalityAdmin(targetUserId, boundaryId, role = 'steward') {
  try {
    const { data, error } = await supabaseFetch.rpc('grant_locality_admin', {
      p_target_user: targetUserId,
      p_boundary: boundaryId,
      p_role: role
    });
    if (error) {
      console.error('grantLocalityAdmin error:', error);
      return { success: false, error };
    }
    return { success: true, id: data };
  } catch (err) {
    console.error('grantLocalityAdmin exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Revoke locality admin status via RPC.
 */
export async function revokeLocalityAdmin(targetUserId, boundaryId, role = 'steward') {
  try {
    const { error } = await supabaseFetch.rpc('revoke_locality_admin', {
      p_target_user: targetUserId,
      p_boundary: boundaryId,
      p_role: role
    });
    if (error) {
      console.error('revokeLocalityAdmin error:', error);
      return { success: false, error };
    }
    return { success: true };
  } catch (err) {
    console.error('revokeLocalityAdmin exception:', err);
    return { success: false, error: err };
  }
}

// ---------------------------------------------------------------------------
// Steward Workspace functions
// ---------------------------------------------------------------------------

/**
 * Fetch the tab list for the steward workspace.
 * Admin sees all boundaries with listings; Steward sees only their assigned boundaries.
 * @returns {Promise<Array<{boundary_id, boundary_name, steward_name, total_listings, active_listings}>>}
 */
export async function getStewardWorkspaceAreas() {
  try {
    const { data, error } = await supabaseFetch.rpc('get_steward_workspace_areas');
    if (error) {
      console.error('getStewardWorkspaceAreas error:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('getStewardWorkspaceAreas exception:', err);
    return [];
  }
}

/**
 * Every active steward appointment, with the steward's public name (Roadmap #86, addendum 1).
 * For a platform admin only: locality_admins RLS gives an admin every row (anyone else, only
 * their own). Names come through getPublicProfiles — the sanctioned display-name path, never a
 * direct users read.
 * @returns {Promise<Array<{boundary_id: number, user_id: string, display_name: string|null}>>}
 */
export async function getStewardAppointments() {
  const rows = (await getAllLocalityAdmins()).filter(r => r.role === 'steward');
  if (rows.length === 0) return [];
  const { data: profiles, error } = await getPublicProfiles(rows.map(r => r.user_id));
  if (error) console.error('getStewardAppointments profiles error:', error);
  const nameById = {};
  (profiles || []).forEach(p => { nameById[p.id] = p.display_name; });
  return rows.map(r => ({
    boundary_id: Number(r.boundary_id),
    user_id: r.user_id,
    display_name: nameById[r.user_id] || null
  }));
}

/**
 * Name, tier, parent id and parent name for each boundary — the Steward Workspace's area picker
 * (Roadmap #86, addendum 1 ruling 3; grouped by municipality, Roadmap #87). Two light reads, never
 * get_boundaries_by_ids (it carries full geometry). Chunked like getBoundaryNamesInOrder.
 * @param {number[]} ids
 * @returns {Promise<Object>} map of { [id]: { name, tier, parentId, parentName, countryCode } }
 */
export async function getBoundaryParents(ids) {
  const clean = [...new Set((ids || []).filter(v => v != null).map(Number))];
  if (clean.length === 0) return {};
  const CHUNK = 200;
  try {
    const rows = [];
    for (let i = 0; i < clean.length; i += CHUNK) {
      const { data, error } = await supabaseFetch.query('admin_boundaries', {
        select: 'id,name,tier,parent_id,country_code',
        inFilter: { id: clean.slice(i, i + CHUNK) }
      });
      if (error || !data) return {};
      rows.push(...data);
    }
    const parentNames = await getBoundaryNames(rows.map(r => r.parent_id));
    return rows.reduce((acc, r) => ({
      ...acc,
      [Number(r.id)]: {
        name: r.name,
        tier: r.tier ?? null,
        parentId: r.parent_id != null ? Number(r.parent_id) : null,
        parentName: parentNames[r.parent_id] || null,
        countryCode: r.country_code || null
      }
    }), {});
  } catch (err) {
    console.error('getBoundaryParents exception:', err);
    return {};
  }
}

// The columns the Steward Workspace's listing table and form read — one list for the area and the
// national reads, so the two cannot drift.
const WORKSPACE_LISTING_SELECT = 'id,boundary_id,national_country_code,section,category,name,description,phone,email,website,address,tags,claimed_by_group_id,claim_status,is_active,created_at,updated_at';

/**
 * Fetch all listings for a single boundary, including inactive (for steward editing).
 * Flat list, not grouped by section. Relies on the steward SELECT RLS policy.
 * @param {number} boundaryId
 * @returns {Promise<Array>}
 */
export async function getListingsForSteward(boundaryId) {
  try {
    const { data, error } = await supabaseFetch.query('directory_listings', {
      select: WORKSPACE_LISTING_SELECT,
      eq: { boundary_id: boundaryId },
      order: 'section,name'
    });
    if (error) {
      console.error('getListingsForSteward error:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('getListingsForSteward exception:', err);
    return [];
  }
}

/**
 * Fetch every national listing for one country, including inactive — the admin-only "National"
 * entry in the Steward Workspace (Roadmap #87, addendum 2). A national listing has no area
 * (national_country_code set, boundary_id NULL — 20261004f); only platform admins can write one,
 * and the admin policy lets them read inactive ones.
 * @param {string} countryCode - ISO 3166-1 alpha-2, upper case ('PT')
 * @returns {Promise<Array>}
 */
export async function getNationalListings(countryCode) {
  if (!countryCode) return [];
  try {
    const { data, error } = await supabaseFetch.query('directory_listings', {
      select: WORKSPACE_LISTING_SELECT,
      eq: { national_country_code: countryCode },
      order: 'section,name'
    });
    if (error) {
      console.error('getNationalListings error:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('getNationalListings exception:', err);
    return [];
  }
}

/**
 * The countries that already have a national listing (any state), so the admin's "National"
 * entries include a country none of their areas is in (Roadmap #87, addendum 2).
 * @returns {Promise<string[]>}
 */
export async function getNationalListingCountries() {
  try {
    const { data, error } = await supabaseFetch.query('directory_listings', {
      select: 'national_country_code',
      notNull: ['national_country_code']
    });
    if (error) {
      console.error('getNationalListingCountries error:', error);
      return [];
    }
    return [...new Set((data || []).map(r => r.national_country_code))];
  } catch (err) {
    console.error('getNationalListingCountries exception:', err);
    return [];
  }
}

/**
 * Create a new directory listing. RLS enforces is_steward(boundary_id).
 * @param {Object} payload - { boundary_id, section, category, name, description, phone, email, website, address, tags }
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function createListing(payload) {
  try {
    const { error } = await supabaseFetch.insert('directory_listings', payload);
    if (error) {
      console.error('createListing error:', error);
      return { success: false, error };
    }
    return { success: true };
  } catch (err) {
    console.error('createListing exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Update a directory listing by id. RLS enforces is_steward(boundary_id).
 * @param {string} id - UUID of the listing
 * @param {Object} patch - fields to update
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function updateListing(id, patch) {
  try {
    const { error } = await supabaseFetch.update('directory_listings', patch, { eq: { id } });
    if (error) {
      console.error('updateListing error:', error);
      return { success: false, error };
    }
    return { success: true };
  } catch (err) {
    console.error('updateListing exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Propagate a group edit to its linked directory listing (group = source of truth).
 * Name and description key on claimed_by_group_id, so it naturally no-ops when no listing is linked;
 * the area write keys on the ids of linked listings that have an area.
 * Shared fields only: name, description, boundary_id. Never touches contact / category / image.
 * RLS enforces is_steward(boundary_id) — sync only lands when the editor is the boundary Steward
 * (for a national listing, is_steward(NULL) is false, so only an admin's write lands);
 * otherwise the card goes stale (acceptable: non-atomic, never corrupt).
 * A national listing (national_country_code set, no area — 20261004f) never takes an area: the
 * database would refuse the whole write, so the area goes in a second write limited to linked
 * listings that have one (Roadmap #87, R7 precondition). Name and description sync either way.
 * @param {string} groupId - communities.id
 * @param {Object} patch - subset of { name, description, boundary_id }
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function syncLinkedListing(groupId, patch) {
  try {
    const { boundary_id: boundaryId, ...shared } = patch || {};
    if (Object.keys(shared).length > 0) {
      const { error } = await supabaseFetch.update('directory_listings', shared, {
        eq: { claimed_by_group_id: groupId }
      });
      if (error) {
        console.error('syncLinkedListing error:', error);
        return { success: false, error };
      }
    }
    if (boundaryId == null) return { success: true };

    // The read sees what the caller may see (active rows, or rows they steward); the write stays
    // RLS-gated as before.
    const { data: linked, error: readError } = await supabaseFetch.query('directory_listings', {
      select: 'id,national_country_code',
      eq: { claimed_by_group_id: groupId }
    });
    if (readError) {
      console.error('syncLinkedListing read error:', readError);
      return { success: false, error: readError };
    }
    const withArea = (linked || []).filter(l => !l.national_country_code).map(l => l.id);
    if (withArea.length === 0) return { success: true };
    const { error } = await supabaseFetch.update('directory_listings', { boundary_id: boundaryId }, {
      inFilter: { id: withArea }
    });
    if (error) {
      console.error('syncLinkedListing error:', error);
      return { success: false, error };
    }
    return { success: true };
  } catch (err) {
    console.error('syncLinkedListing exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Resolve admin_boundaries ids → names for display (e.g. reconciliation modal).
 * @param {number[]} ids
 * @returns {Promise<Object>} map of { [id]: name }
 */
export async function getBoundaryNames(ids) {
  const clean = [...new Set((ids || []).filter(v => v != null))];
  if (clean.length === 0) return {};
  try {
    const { data, error } = await supabaseFetch.query('admin_boundaries', {
      select: 'id,name',
      inFilter: { id: clean }
    });
    if (error || !data) return {};
    return data.reduce((acc, b) => ({ ...acc, [b.id]: b.name }), {});
  } catch (err) {
    console.error('getBoundaryNames exception:', err);
    return {};
  }
}

/**
 * Resolve admin_boundaries ids → [{ id, name }] in the order given, for callers that must tell a
 * failed lookup from "none" (group content locality, #72). Unlike getBoundaryNames it reports
 * errors, and it queries in chunks so a group with thousands of boundaries stays within URL
 * limits. Ids that do not resolve are dropped.
 * @param {number[]} ids
 * @returns {Promise<{ data: Array<{id: number, name: string}>, error: any }>}
 */
export async function getBoundaryNamesInOrder(ids) {
  const clean = [...new Set((ids || []).filter(v => v != null).map(Number))];
  if (clean.length === 0) return { data: [], error: null };
  const CHUNK = 200;
  try {
    const byId = {};
    for (let i = 0; i < clean.length; i += CHUNK) {
      const { data, error } = await supabaseFetch.query('admin_boundaries', {
        select: 'id,name',
        inFilter: { id: clean.slice(i, i + CHUNK) }
      });
      if (error || !data) return { data: [], error: error || new Error('boundary names did not resolve') };
      data.forEach(b => { byId[Number(b.id)] = b.name; });
    }
    return { data: clean.filter(id => id in byId).map(id => ({ id, name: byId[id] })), error: null };
  } catch (err) {
    console.error('getBoundaryNamesInOrder exception:', err);
    return { data: [], error: err };
  }
}

/**
 * An area's parishes, named, in their saved order, with the area's own (base) parish as the
 * default — for "Publishing to" in the article and event forms and "Your own parish" in My
 * Locations (docs/briefs/2026-09-29-own-parish-everywhere-addendum-2.md). The base parish is
 * listed first if the list somehow lacks it (the database refuses that from migration 20260929).
 * @param {{boundary_ids?: Array<number|string>, primary_boundary_id?: number|string}|null} area
 * @returns {Promise<{ options: Array<{id: number, name: string}>, defaultId: number|null, error: any }>}
 */
export async function getAreaParishes(area) {
  const defaultId = baseParishId(area);
  const ids = (area?.boundary_ids || []).map(Number);
  const ordered = defaultId != null && !ids.includes(defaultId) ? [defaultId, ...ids] : ids;
  if (ordered.length === 0) return { options: [], defaultId, error: null };
  const { data, error } = await getBoundaryNamesInOrder(ordered);
  return { options: data || [], defaultId, error };
}

/**
 * Write reconciled shared fields to a group (communities). RLS enforces owner/community-admin,
 * so this only lands for groups the Steward owns — the reconciliation picker is scoped to those.
 * Null ids are dropped from boundary_ids, and boundary_ids is dropped if none remain, so this
 * cannot clear a group's areas.
 * @param {string} groupId - communities.id
 * @param {Object} patch - subset of { name, description, boundary_ids }
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function updateGroupSharedFields(groupId, patch) {
  try {
    // Never write a missing area into a group's areas — e.g. a national listing has none
    // (Roadmap #87, R7 precondition).
    const safe = { ...patch };
    if ('boundary_ids' in safe) {
      const ids = (Array.isArray(safe.boundary_ids) ? safe.boundary_ids : []).filter(id => id != null);
      if (ids.length > 0) safe.boundary_ids = ids;
      else delete safe.boundary_ids;
    }
    if (Object.keys(safe).length === 0) return { success: true };
    const { error } = await supabaseFetch.update('communities', safe, { eq: { id: groupId } });
    if (error) {
      console.error('updateGroupSharedFields error:', error);
      return { success: false, error };
    }
    return { success: true };
  } catch (err) {
    console.error('updateGroupSharedFields exception:', err);
    return { success: false, error: err };
  }
}

/**
 * Soft-delete a listing (set is_active = false). RLS enforces is_steward(boundary_id).
 * @param {string} id - UUID of the listing
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function softDeleteListing(id) {
  return updateListing(id, { is_active: false });
}

/**
 * Restore a soft-deleted listing (set is_active = true). RLS enforces is_steward(boundary_id).
 * @param {string} id - UUID of the listing
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export async function restoreListing(id) {
  return updateListing(id, { is_active: true });
}

/**
 * Get the caller's own active stewardship rows (for ProfileMenu visibility check).
 * RLS returns only the caller's own rows.
 * @returns {Promise<Array<{boundary_id: number, role: string}>>}
 */
export async function getMyStewardships() {
  try {
    const { data, error } = await supabaseFetch.query('locality_admins', {
      select: 'boundary_id,role',
      eq: { is_active: true, role: 'steward' }
    });
    if (error || !data) return [];
    return data;
  } catch (err) {
    console.error('getMyStewardships exception:', err);
    return [];
  }
}

/**
 * Fetch hero image data for a boundary.
 * @param {number} boundaryId
 * @returns {Promise<{hero_image_url: string|null, hero_image_credit: string|null}>}
 */
export async function getLocalityHeroImage(boundaryId) {
  try {
    const { data, error } = await supabaseFetch.query('admin_boundaries', {
      select: 'hero_image_url,hero_image_credit',
      eq: { id: boundaryId },
      single: true
    });
    if (error || !data) return { hero_image_url: null, hero_image_credit: null };
    return data;
  } catch (err) {
    return { hero_image_url: null, hero_image_credit: null };
  }
}
